import React, { useContext, useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  Bars3Icon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { navigation, classNames } from "../utils/constants";
import AuthContext from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import RequireAuthPopup from "./RequireAuthPopup";
import Logo from "../assets/1.svg";

export const Navbar = ({ sidebarOpen, setSidebarOpen }) => {
  const { user, setUser } = useContext(AuthContext);
  const [isSignOutOpen, setIsSignOutOpen] = useState(false);
  const [authPopupOpen, setAuthPopupOpen] = useState(false); // Add state for auth popup
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) setUser(null); // Clear user state if not in localStorage
  }, [setUser]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsSignOutOpen(false);
      localStorage.removeItem("user");
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) {
      const name = user.email.split("@")[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return "User";
  };

  const UserButton = ({ className = "" }) => (
    <button
      className={classNames(
        "group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold",
        user
          ? "bg-gray-50 text-indigo-600"
          : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
        className,
      )}
      onClick={() => (user ? setIsSignOutOpen(true) : navigate("/login"))}
    >
      <UserCircleIcon
        className={classNames(
          "size-6 shrink-0",
          user
            ? "text-indigo-600"
            : "text-gray-400 group-hover:text-indigo-600",
        )}
        aria-hidden="true"
      />
      {user ? getDisplayName() : "Sign in"}
    </button>
  );

  const handleProtectedClick = (event, href) => {
    if (!user) {
      event.preventDefault();
      setAuthPopupOpen(true);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <RequireAuthPopup
        isOpen={authPopupOpen}
        onClose={() => setAuthPopupOpen(false)}
      />

      {/* Sign Out Dialog */}
      <Dialog
        open={isSignOutOpen}
        onClose={() => setIsSignOutOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
              <p className="text-center text-base text-gray-900">
                Are you sure you want to sign out?
              </p>
              <div className="mt-6 flex justify-center gap-4">
                <button
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
                <button
                  className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  onClick={() => setIsSignOutOpen(false)}
                >
                  Stay
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Sidebar for mobile */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="size-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
              <div className="flex h-16 shrink-0 items-center">
                <img className="h-16 w-auto mt-4" src={Logo} alt="Weve" />{" "}
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            className={classNames(
                              item.current
                                ? "bg-gray-50 text-indigo-600"
                                : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                              "group flex gap-x-3 rounded-md p-2 text-sm font-semibold",
                            )}
                          >
                            <item.icon
                              className={classNames(
                                item.current
                                  ? "text-indigo-600"
                                  : "text-gray-400 group-hover:text-indigo-600",
                                "size-6 shrink-0",
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto">
                    <UserButton />
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
          <div className="flex h-16 shrink-0 items-center">
          <img 
  className="h-20 w-auto mt-6" 
  src={Logo} 
  alt="Weve" 
/>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className={classNames(
                          item.current
                            ? "bg-gray-50 text-indigo-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                          "group flex gap-x-3 rounded-md p-2 text-sm font-semibold",
                        )}
                        onClick={(e) => handleProtectedClick(e, item.href)}
                      >
                        <item.icon
                          className={classNames(
                            item.current
                              ? "text-indigo-600"
                              : "text-gray-400 group-hover:text-indigo-600",
                            "size-6 shrink-0",
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="mt-auto">
                <UserButton />
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Top Navbar for mobile */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="size-6" aria-hidden="true" />
        </button>
        <div className="flex-1 text-sm font-semibold text-gray-900">
          Dashboard
        </div>
        <UserButton />
      </div>
    </>
  );
};
